import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ExcelUploadSurface } from '@bliks-io/ui';
import { FieldExplainer, type ExplainerSection } from './FieldExplainer';
import { OrAddManually } from './OrAddManually';

interface UploadEmptyStateProps {
  headingKey: string;
  sections: ExplainerSection[];
  downloadTemplate: () => void;
  /** Parses the file into the step's draft state; returns the parsed row count */
  parse: (file: File) => Promise<number>;
  onAddManually: () => void;
  /** i18n key of the manual-add action, named after the entity */
  addLabelKey: string;
  /** Further import buttons under the dropzone */
  importActions?: ReactNode;
}

/**
 * The empty state of every wizard upload table: explains each template column
 * inline, offers the template download and an inline dropzone, with a single
 * manual row as the quiet alternative. Once rows exist the table takes over
 * and this disappears.
 */
export function UploadEmptyState({
  headingKey,
  sections,
  downloadTemplate,
  parse,
  onAddManually,
  addLabelKey,
  importActions
}: UploadEmptyStateProps) {
  const { t } = useTranslation();

  return (
    <div>
      <h3 className="text-[15px] font-bold text-foreground">{t(headingKey)}</h3>

      <FieldExplainer sections={sections} />

      <div className="mt-[26px] space-y-3">
        <ExcelUploadSurface
          downloadTemplate={downloadTemplate}
          parse={parse}
          dropZoneClassName="bg-white"
        />
        {importActions && <div className="flex justify-center">{importActions}</div>}
      </div>

      <OrAddManually onAddManually={onAddManually} addLabelKey={addLabelKey} className="mt-4" />
    </div>
  );
}
